import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, Users, CreditCard, Image, Settings, LogOut, 
  TrendingUp, DollarSign, Eye, Edit2, Trash2, Plus, Minus,
  ChevronLeft, ChevronRight, Search, Lock, X, Check, Download, Mail, Filter, Menu
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple chart component
const SimpleBarChart = ({ data, dataKey, color = "#D4AF37" }) => {
  const maxValue = Math.max(...data.map(d => d[dataKey]), 1);
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((item) => (
        <div key={item.date || item[dataKey]} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full rounded-t transition-all duration-300"
            style={{ 
              height: `${(item[dataKey] / maxValue) * 100}%`,
              backgroundColor: color,
              minHeight: item[dataKey] > 0 ? '4px' : '0'
            }}
          />
          <span className="text-[10px] text-white/40">{item.date?.slice(5) || ''}</span>
        </div>
      ))}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, title, value, subValue, color = "gold" }) => (
  <div className="bg-white/5 rounded-xl p-5 border border-white/10">
    <div className="flex items-center justify-between mb-3">
      <div className={`p-2 rounded-lg ${color === 'gold' ? 'bg-[#D4AF37]/20' : 'bg-white/10'}`}>
        <Icon className={`w-5 h-5 ${color === 'gold' ? 'text-[#D4AF37]' : 'text-white/60'}`} />
      </div>
      {subValue && (
        <span className="text-xs text-green-400 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> {subValue}
        </span>
      )}
    </div>
    <p className="text-white/60 text-sm">{title}</p>
    <p className="text-2xl font-bold text-white mt-1">{value}</p>
  </div>
);

// Confirmation Modal Component
const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Confirm", 
  confirmColor = "gold",
  icon: Icon,
  children 
}) => {
  if (!isOpen) return null;
  
  const colorClasses = {
    gold: "bg-[#D4AF37] hover:bg-[#C4A030] text-black",
    red: "bg-red-500 hover:bg-red-600 text-white",
    green: "bg-green-500 hover:bg-green-600 text-white",
    orange: "bg-orange-500 hover:bg-orange-600 text-white"
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#111] rounded-2xl p-6 w-full max-w-md border border-white/10"
      >
        <div className="flex items-center gap-3 mb-4">
          {Icon && (
            <div className={`p-2 rounded-lg ${confirmColor === 'red' ? 'bg-red-500/20' : confirmColor === 'green' ? 'bg-green-500/20' : confirmColor === 'orange' ? 'bg-orange-500/20' : 'bg-[#D4AF37]/20'}`}>
              <Icon className={`w-5 h-5 ${confirmColor === 'red' ? 'text-red-400' : confirmColor === 'green' ? 'text-green-400' : confirmColor === 'orange' ? 'text-orange-400' : 'text-[#D4AF37]'}`} />
            </div>
          )}
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
        
        <p className="text-white/60 mb-4">{message}</p>
        
        {children}
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 py-2 font-semibold rounded-lg transition-colors ${colorClasses[confirmColor]}`}
          >
            {confirmText}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// CRM Tab Component
const CRMTab = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [filterCounts, setFilterCounts] = useState({});
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const filters = [
    { id: 'all', label: 'All Users' },
    { id: 'new_7d', label: 'New (7 days)' },
    { id: 'zero_credits', label: 'Zero Credits' },
    { id: 'high_value', label: 'High Value' },
    { id: 'referred', label: 'Referred' },
    { id: 'vault', label: 'Vault Members' },
  ];

  const fetchCRMUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/crm/users`, {
        params: { token, filter: activeFilter, search, limit: 200 }
      });
      setUsers(res.data.users || []);
      setFilterCounts(res.data.filter_counts || {});
    } catch (err) {
      // CRM fetch error - silenced
    }
    setLoading(false);
  }, [token, activeFilter, search]);

  useEffect(() => {
    fetchCRMUsers();
  }, [fetchCRMUsers]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/crm/export-csv`, {
        params: { token, filter: activeFilter },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `starframe-users-${activeFilter}-${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed');
    }
    setExporting(false);
  };

  return (
    <motion.div
      key="crm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white" data-testid="crm-title">CRM / Email Management</h2>
          <p className="text-white/40 text-sm mt-1">{users.length} users in current view</p>
        </div>
        <button
          data-testid="crm-export-csv"
          onClick={handleExportCSV}
          disabled={exporting}
          className="bg-[#D4AF37] hover:bg-[#c9a432] text-black font-bold px-5 py-2.5 rounded-xl flex items-center gap-2 text-sm transition-colors disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exporting...' : `Export CSV (${activeFilter})`}
        </button>
      </div>

      {/* Filter Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map(f => (
          <button
            key={f.id}
            data-testid={`crm-filter-${f.id}`}
            onClick={() => setActiveFilter(f.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              activeFilter === f.id
                ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37]'
                : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
            }`}
          >
            {f.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              activeFilter === f.id ? 'bg-[#D4AF37]/30' : 'bg-white/10'
            }`}>
              {filterCounts[f.id] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
        <input
          type="text"
          data-testid="crm-search"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none text-sm"
        />
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="text-center py-12 text-white/40">Loading...</div>
      ) : (
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="crm-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-white/40 font-medium">Name</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-white/40 font-medium">Email</th>
                  <th className="text-center p-4 text-xs uppercase tracking-wider text-white/40 font-medium">Credits</th>
                  <th className="text-center p-4 text-xs uppercase tracking-wider text-white/40 font-medium">Portraits</th>
                  <th className="text-center p-4 text-xs uppercase tracking-wider text-white/40 font-medium">Spent</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-white/40 font-medium">Signup</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-white/40 font-medium">Last Active</th>
                  <th className="text-center p-4 text-xs uppercase tracking-wider text-white/40 font-medium">Referred</th>
                  <th className="text-center p-4 text-xs uppercase tracking-wider text-white/40 font-medium">Vault</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="p-4 text-white text-sm font-medium">{user.name || '—'}</td>
                    <td className="p-4 text-white/70 text-sm">{user.email}</td>
                    <td className="p-4 text-center">
                      <span className={`text-sm font-semibold ${user.credits <= 0 ? 'text-red-400' : user.credits >= 20 ? 'text-green-400' : 'text-white'}`}>
                        {user.credits}
                      </span>
                    </td>
                    <td className="p-4 text-center text-white/60 text-sm">{user.portrait_count || 0}</td>
                    <td className="p-4 text-center text-[#D4AF37] text-sm font-medium">
                      {user.total_spent > 0 ? `$${user.total_spent.toFixed(2)}` : '—'}
                    </td>
                    <td className="p-4 text-white/40 text-xs">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-4 text-white/40 text-xs">
                      {user.last_active ? new Date(user.last_active).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-4 text-center">
                      {user.referred_by ? (
                        <span className="text-[10px] bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full">Yes</span>
                      ) : (
                        <span className="text-white/20 text-xs">—</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {user.vault_active ? (
                        <span className="text-[10px] bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full font-semibold">Active</span>
                      ) : (
                        <span className="text-white/20 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {users.length === 0 && (
            <div className="text-center py-12 text-white/30 text-sm">No users match this filter</div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      onLogin(response.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
          <p className="text-white/60">StarFrame AI Management</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/5 rounded-2xl p-8 border border-white/10">
          <div className="mb-6">
            <label className="block text-white/60 text-sm mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
              placeholder="admin@example.com"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-white/60 text-sm mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-black font-semibold py-3 rounded-lg hover:bg-[#C4A030] transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

// Change Password Modal
const ChangePasswordModal = ({ token, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        token,
        current_password: currentPassword,
        new_password: newPassword
      });
      setSuccess(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#111] rounded-2xl p-6 w-full max-w-md border border-white/10"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">Change Password</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="text-center py-8">
            <Check className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <p className="text-white">Password changed successfully!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-white/60 text-sm mb-2">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-white/60 text-sm mb-2">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-white/60 text-sm mb-2">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
                required
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-black font-semibold py-3 rounded-lg hover:bg-[#C4A030] transition-colors disabled:opacity-50"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// User Edit Modal
const UserEditModal = ({ user, token, onClose, onSave }) => {
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [credits, setCredits] = useState(user.credits || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/admin/users/update`, {
        token,
        user_id: user.id,
        name,
        email,
        credits: parseInt(credits)
      });
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#111] rounded-2xl p-6 w-full max-w-md border border-white/10"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">Edit User</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-white/60 text-sm mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div className="mb-4">
            <label className="block text-white/60 text-sm mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div className="mb-6">
            <label className="block text-white/60 text-sm mb-2">Credits</label>
            <input
              type="number"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-white/10 text-white font-semibold py-3 rounded-lg hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#D4AF37] text-black font-semibold py-3 rounded-lg hover:bg-[#C4A030] transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [token, setToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(0);
  
  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Confirmation modal states
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: null, // 'delete' | 'addCredits' | 'removeCredits'
    user: null,
    amount: 0,
    reason: ''
  });

  const verifyToken = useCallback(async () => {
    if (!token) {
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/verify-token?token=${token}`);
      setIsAuthenticated(response.data.valid);
    } catch {
      setIsAuthenticated(false);
      setToken('');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [overviewRes, chartRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview?token=${token}`),
        axios.get(`${API}/admin/analytics/chart-data?token=${token}&days=7`)
      ]);
      setAnalytics(overviewRes.data);
      setChartData(chartRes.data.chart_data);
    } catch (err) {
      // analytics fetch error
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/admin/users?token=${token}&skip=${usersPage * 10}&limit=10&search=${userSearch}`
      );
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (err) {
      // users fetch error
    }
  }, [token, usersPage, userSearch]);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/admin/transactions?token=${token}&skip=${txPage * 10}&limit=10`
      );
      setTransactions(response.data.transactions);
      setTxTotal(response.data.total);
    } catch (err) {
      // transactions fetch error
    }
  }, [token, txPage]);

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === 'overview') fetchAnalytics();
      if (activeTab === 'users') fetchUsers();
      if (activeTab === 'transactions') fetchTransactions();
    }
  }, [isAuthenticated, activeTab, fetchAnalytics, fetchUsers, fetchTransactions]);

  const handleLogout = async () => {
    await axios.post(`${API}/admin/logout?token=${token}`);
    setToken('');
    setIsAuthenticated(false);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}?token=${token}`);
      fetchUsers();
      setConfirmModal({ isOpen: false, type: null, user: null, amount: 0, reason: '' });
    } catch (err) {
      alert('Failed to delete user');
    }
  };

  const handleAddCredits = async () => {
    const { user, amount, reason } = confirmModal;
    if (!reason.trim()) {
      alert('Please provide a reason');
      return;
    }

    try {
      await axios.post(`${API}/admin/users/add-credits`, {
        token,
        user_id: user.id,
        amount,
        reason
      });
      fetchUsers();
      setConfirmModal({ isOpen: false, type: null, user: null, amount: 0, reason: '' });
    } catch (err) {
      alert('Failed to update credits');
    }
  };

  const openConfirmModal = (type, user, amount = 0) => {
    setConfirmModal({
      isOpen: true,
      type,
      user,
      amount,
      reason: ''
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false, type: null, user: null, amount: 0, reason: '' });
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/admin/users/bulk-delete`, {
        token,
        user_ids: selectedUsers
      });
      setSelectedUsers([]);
      setShowBulkDeleteConfirm(false);
      fetchUsers();
    } catch (err) {
      alert('Failed to delete users');
    }
    setBulkDeleting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={(t) => { setToken(t); setIsAuthenticated(true); }} />;
  }

  const sidebarItems = [
    { id: 'overview', icon: BarChart3, label: 'Overview' },
    { id: 'crm', icon: Mail, label: 'CRM / Emails' },
    { id: 'users', icon: Users, label: 'Users' },
    { id: 'transactions', icon: CreditCard, label: 'Transactions' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
          data-testid="admin-sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:static top-0 left-0 bottom-0 z-50
          w-64 bg-[#0a0a0a] border-r border-white/10 flex flex-col
          transform transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        `}
        data-testid="admin-sidebar"
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">StarFrame AI</h1>
            <p className="text-white/40 text-sm">Admin Dashboard</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1.5 rounded-lg text-white/60 hover:bg-white/5 lg:hidden"
            aria-label="Close menu"
            data-testid="admin-sidebar-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          {sidebarItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                activeTab === item.id 
                  ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={() => setShowPasswordModal(true)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/60 hover:bg-white/5 hover:text-white transition-colors mb-1"
          >
            <Lock className="w-5 h-5" />
            Change Password
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-[#0a0a0a] border-b border-white/10">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-white hover:bg-white/5"
            aria-label="Open menu"
            data-testid="admin-mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1 text-center">
            <p className="text-sm font-semibold text-white capitalize">{activeTab}</p>
          </div>
          <div className="w-9" />{/* spacer for balance */}
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          <AnimatePresence mode="wait">
            {/* Overview Tab */}
            {activeTab === 'overview' && analytics && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <h2 className="text-2xl font-bold text-white mb-6">Dashboard Overview</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <StatCard 
                    icon={Users} 
                    title="Total Users" 
                    value={analytics.users.total}
                    subValue={`+${analytics.users.today} today`}
                  />
                  <StatCard 
                    icon={Image} 
                    title="Portraits Generated" 
                    value={analytics.portraits.total}
                    subValue={`+${analytics.portraits.today} today`}
                  />
                  <StatCard 
                    icon={DollarSign} 
                    title="Total Revenue" 
                    value={`$${analytics.revenue.total.toFixed(2)}`}
                    subValue={`+$${analytics.revenue.today.toFixed(2)} today`}
                  />
                  <StatCard 
                    icon={CreditCard} 
                    title="Credits in Circulation" 
                    value={analytics.credits_in_circulation}
                    color="white"
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Charts */}
                  <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-4">New Users (7 days)</h3>
                    <SimpleBarChart data={chartData} dataKey="users" color="#D4AF37" />
                  </div>
                  
                  <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-4">Revenue (7 days)</h3>
                    <SimpleBarChart data={chartData} dataKey="revenue" color="#22C55E" />
                  </div>

                  {/* Style Popularity */}
                  <div className="bg-white/5 rounded-xl p-6 border border-white/10 lg:col-span-2">
                    <h3 className="text-lg font-semibold text-white mb-4">Style Popularity</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {analytics.style_popularity.map(style => (
                        <div key={style._id} className="bg-white/5 rounded-lg p-4 text-center">
                          <p className="text-[#D4AF37] font-semibold capitalize">{style._id || 'Unknown'}</p>
                          <p className="text-2xl font-bold text-white">{style.count}</p>
                          <p className="text-white/40 text-sm">generations</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* CRM / Emails Tab */}
            {activeTab === 'crm' && <CRMTab token={token} />}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <motion.div
                key="users"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">User Management</h2>
                  <div className="relative">
                    <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => { setUserSearch(e.target.value); setUsersPage(0); }}
                      className="pl-10 pr-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none w-64"
                    />
                  </div>
                </div>

                {/* Bulk Action Bar */}
                <AnimatePresence>
                  {selectedUsers.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mb-4 bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center justify-between"
                      data-testid="bulk-action-bar"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-white text-sm font-medium">
                          {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                        </span>
                        <button onClick={() => setSelectedUsers([])} className="text-white/50 text-xs hover:text-white transition-colors">
                          Clear
                        </button>
                      </div>
                      <button
                        data-testid="bulk-delete-btn"
                        onClick={() => setShowBulkDeleteConfirm(true)}
                        className="bg-red-500 hover:bg-red-400 text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Selected ({selectedUsers.length})
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="p-4 w-12">
                          <input
                            type="checkbox"
                            data-testid="select-all-users"
                            checked={users.length > 0 && selectedUsers.length === users.length}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded border-white/30 accent-[#D4AF37] cursor-pointer"
                          />
                        </th>
                        <th className="text-left p-4 text-white/60 font-medium">User</th>
                        <th className="text-left p-4 text-white/60 font-medium">Credits</th>
                        <th className="text-left p-4 text-white/60 font-medium">Portraits</th>
                        <th className="text-left p-4 text-white/60 font-medium">Joined</th>
                        <th className="text-right p-4 text-white/60 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${selectedUsers.includes(user.id) ? 'bg-red-500/5' : ''}`}>
                          <td className="p-4">
                            <input
                              type="checkbox"
                              data-testid={`select-user-${user.id}`}
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => toggleUserSelection(user.id)}
                              className="w-4 h-4 rounded border-white/30 accent-[#D4AF37] cursor-pointer"
                            />
                          </td>
                          <td className="p-4">
                            <p className="text-white font-medium">{user.name}</p>
                            <p className="text-white/40 text-sm">{user.email}</p>
                          </td>
                          <td className="p-4">
                            <span className="text-[#D4AF37] font-semibold">{user.credits}</span>
                          </td>
                          <td className="p-4 text-white/60">{user.portrait_count || 0}</td>
                          <td className="p-4 text-white/60 text-sm">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openConfirmModal('addCredits', user, 10)}
                                className="p-2 text-green-400 hover:bg-green-400/20 rounded-lg transition-colors"
                                title="Add credits"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openConfirmModal('removeCredits', user, -10)}
                                className="p-2 text-orange-400 hover:bg-orange-400/20 rounded-lg transition-colors"
                                title="Remove credits"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setEditingUser(user)}
                                className="p-2 text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                                title="Edit user"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openConfirmModal('delete', user)}
                                className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                                title="Delete user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="flex items-center justify-between p-4 border-t border-white/10">
                    <p className="text-white/40 text-sm">
                      Showing {usersPage * 10 + 1}-{Math.min((usersPage + 1) * 10, usersTotal)} of {usersTotal}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setUsersPage(p => Math.max(0, p - 1))}
                        disabled={usersPage === 0}
                        className="p-2 bg-white/5 rounded-lg text-white/60 hover:bg-white/10 disabled:opacity-30"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setUsersPage(p => p + 1)}
                        disabled={(usersPage + 1) * 10 >= usersTotal}
                        className="p-2 bg-white/5 rounded-lg text-white/60 hover:bg-white/10 disabled:opacity-30"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            <AnimatePresence>
              {showBulkDeleteConfirm && (
                <motion.div
                  data-testid="bulk-delete-modal"
                  className="fixed inset-0 z-[100] flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => !bulkDeleting && setShowBulkDeleteConfirm(false)} />
                  <motion.div
                    className="relative bg-[#0a0a0a] border border-red-500/30 rounded-2xl p-6 max-w-sm w-full mx-4"
                    initial={{ scale: 0.9, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.9, y: 20 }}
                  >
                    <div className="absolute top-0 left-0 right-0 h-1 bg-red-500/50 rounded-t-2xl" />
                    <div className="text-center mb-5">
                      <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Trash2 className="w-7 h-7 text-red-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white">Delete {selectedUsers.length} User{selectedUsers.length !== 1 ? 's' : ''}?</h3>
                      <p className="text-white/50 text-sm mt-2">
                        This will permanently delete {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} and all their portraits. This cannot be undone.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        data-testid="bulk-delete-cancel"
                        onClick={() => setShowBulkDeleteConfirm(false)}
                        disabled={bulkDeleting}
                        className="flex-1 bg-white/10 hover:bg-white/20 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        data-testid="bulk-delete-confirm"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleting}
                        className="flex-1 bg-red-500 hover:bg-red-400 text-white py-2.5 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2"
                      >
                        {bulkDeleting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : `Delete ${selectedUsers.length}`}
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <motion.div
                key="transactions"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <h2 className="text-2xl font-bold text-white mb-6">Transactions</h2>

                <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-4 text-white/60 font-medium">User</th>
                        <th className="text-left p-4 text-white/60 font-medium">Package</th>
                        <th className="text-left p-4 text-white/60 font-medium">Amount</th>
                        <th className="text-left p-4 text-white/60 font-medium">Status</th>
                        <th className="text-left p-4 text-white/60 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(tx => (
                        <tr key={tx.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-4">
                            <p className="text-white">{tx.user?.name || 'Unknown'}</p>
                            <p className="text-white/40 text-sm">{tx.user?.email || ''}</p>
                          </td>
                          <td className="p-4 text-white capitalize">{tx.package_id}</td>
                          <td className="p-4">
                            <span className="text-[#D4AF37] font-semibold">${tx.amount}</span>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              tx.payment_status === 'paid' 
                                ? 'bg-green-500/20 text-green-400'
                                : tx.payment_status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {tx.payment_status}
                            </span>
                          </td>
                          <td className="p-4 text-white/60 text-sm">
                            {new Date(tx.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      {transactions.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-white/40">
                            No transactions yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="flex items-center justify-between p-4 border-t border-white/10">
                    <p className="text-white/40 text-sm">
                      Showing {txPage * 10 + 1}-{Math.min((txPage + 1) * 10, txTotal)} of {txTotal}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setTxPage(p => Math.max(0, p - 1))}
                        disabled={txPage === 0}
                        className="p-2 bg-white/5 rounded-lg text-white/60 hover:bg-white/10 disabled:opacity-30"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setTxPage(p => p + 1)}
                        disabled={(txPage + 1) * 10 >= txTotal}
                        className="p-2 bg-white/5 rounded-lg text-white/60 hover:bg-white/10 disabled:opacity-30"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <SettingsTab token={token} />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Modals */}
      {showPasswordModal && (
        <ChangePasswordModal 
          token={token} 
          onClose={() => setShowPasswordModal(false)} 
        />
      )}
      
      {editingUser && (
        <UserEditModal
          user={editingUser}
          token={token}
          onClose={() => setEditingUser(null)}
          onSave={() => { setEditingUser(null); fetchUsers(); }}
        />
      )}

      {/* Delete User Confirmation */}
      <ConfirmModal
        isOpen={confirmModal.isOpen && confirmModal.type === 'delete'}
        onClose={closeConfirmModal}
        onConfirm={() => handleDeleteUser(confirmModal.user?.id)}
        title="Delete User"
        message={`Are you sure you want to delete "${confirmModal.user?.name}"? This will permanently remove their account, all portraits, and transaction history.`}
        confirmText="Delete User"
        confirmColor="red"
        icon={Trash2}
      />

      {/* Add Credits Confirmation */}
      <ConfirmModal
        isOpen={confirmModal.isOpen && confirmModal.type === 'addCredits'}
        onClose={closeConfirmModal}
        onConfirm={handleAddCredits}
        title="Add Credits"
        message={`Add credits to ${confirmModal.user?.name}'s account.`}
        confirmText="Add Credits"
        confirmColor="green"
        icon={Plus}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-white/60 text-sm mb-1">Amount</label>
            <input
              type="number"
              value={confirmModal.amount}
              onChange={(e) => setConfirmModal(prev => ({ ...prev, amount: parseInt(e.target.value) || 0 }))}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-green-400 focus:outline-none"
              min="1"
            />
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1">Reason <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={confirmModal.reason}
              onChange={(e) => setConfirmModal(prev => ({ ...prev, reason: e.target.value }))}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-green-400 focus:outline-none"
              placeholder="e.g., Promotional bonus, Compensation, etc."
            />
          </div>
          <p className="text-white/40 text-xs">
            Current balance: <span className="text-[#D4AF37]">{confirmModal.user?.credits || 0}</span> → 
            New balance: <span className="text-green-400">{(confirmModal.user?.credits || 0) + (confirmModal.amount || 0)}</span>
          </p>
        </div>
      </ConfirmModal>

      {/* Remove Credits Confirmation */}
      <ConfirmModal
        isOpen={confirmModal.isOpen && confirmModal.type === 'removeCredits'}
        onClose={closeConfirmModal}
        onConfirm={handleAddCredits}
        title="Remove Credits"
        message={`Remove credits from ${confirmModal.user?.name}'s account.`}
        confirmText="Remove Credits"
        confirmColor="orange"
        icon={Minus}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-white/60 text-sm mb-1">Amount to Remove</label>
            <input
              type="number"
              value={Math.abs(confirmModal.amount)}
              onChange={(e) => setConfirmModal(prev => ({ ...prev, amount: -Math.abs(parseInt(e.target.value) || 0) }))}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-orange-400 focus:outline-none"
              min="1"
            />
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1">Reason <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={confirmModal.reason}
              onChange={(e) => setConfirmModal(prev => ({ ...prev, reason: e.target.value }))}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-orange-400 focus:outline-none"
              placeholder="e.g., Refund adjustment, Policy violation, etc."
            />
          </div>
          <p className="text-white/40 text-xs">
            Current balance: <span className="text-[#D4AF37]">{confirmModal.user?.credits || 0}</span> → 
            New balance: <span className="text-orange-400">{Math.max(0, (confirmModal.user?.credits || 0) + (confirmModal.amount || 0))}</span>
          </p>
        </div>
      </ConfirmModal>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ token }) => {
  const [packages, setPackages] = useState({});
  const [apiConfig, setApiConfig] = useState({});
  const [customApis, setCustomApis] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editingApi, setEditingApi] = useState(false);
  const [addingApi, setAddingApi] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form states for package editing
  const [pkgName, setPkgName] = useState('');
  const [pkgCredits, setPkgCredits] = useState(0);
  const [pkgBonus, setPkgBonus] = useState(0);
  const [pkgPrice, setPkgPrice] = useState(0);

  // Form states for API config
  const [stripeKey, setStripeKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [emergentKey, setEmergentKey] = useState('');
  const [falKey, setFalKey] = useState('');
  const [activeApi, setActiveApi] = useState('gemini_nano_banana');
  
  // Form states for new custom API
  const [newApiName, setNewApiName] = useState('');
  const [newApiKey, setNewApiKey] = useState('');
  const [newApiDescription, setNewApiDescription] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [pkgRes, apiRes] = await Promise.all([
          axios.get(`${API}/admin/settings/packages?token=${token}`),
          axios.get(`${API}/admin/settings/api-config?token=${token}`)
        ]);
        setPackages(pkgRes.data.packages);
        setApiConfig(apiRes.data.config);
        setCustomApis(apiRes.data.custom_apis || []);
        setActiveApi(apiRes.data.config.active_image_api || 'gemini_nano_banana');
      } catch (err) {
        // settings fetch error
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [token]);

  const handleEditPackage = (id, pkg) => {
    setEditingPackage(id);
    setPkgName(pkg.name);
    setPkgCredits(pkg.credits);
    setPkgBonus(pkg.bonus);
    setPkgPrice(pkg.price);
  };

  const handleSavePackage = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/settings/packages/update`, {
        token,
        package_id: editingPackage,
        name: pkgName,
        credits: parseInt(pkgCredits),
        bonus: parseInt(pkgBonus),
        price: parseFloat(pkgPrice)
      });
      setPackages(response.data.packages);
      setEditingPackage(null);
    } catch (err) {
      alert('Failed to save package: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveApiConfig = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/settings/api-config/update`, {
        token,
        stripe_key: stripeKey || undefined,
        openai_key: openaiKey || undefined,
        emergent_key: emergentKey || undefined,
        fal_key: falKey || undefined,
        active_image_api: activeApi
      });
      setApiConfig(response.data.config);
      setEditingApi(false);
      setStripeKey('');
      setOpenaiKey('');
      setEmergentKey('');
      setFalKey('');
      alert('API configuration updated successfully!');
    } catch (err) {
      alert('Failed to save API config: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAddCustomApi = async () => {
    if (!newApiName.trim() || !newApiKey.trim()) {
      alert('Please provide both API name and key');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/settings/api-config/add-custom`, {
        token,
        name: newApiName,
        key: newApiKey,
        description: newApiDescription
      });
      setCustomApis(response.data.custom_apis);
      setAddingApi(false);
      setNewApiName('');
      setNewApiKey('');
      setNewApiDescription('');
      alert('Custom API added successfully!');
    } catch (err) {
      alert('Failed to add API: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomApi = async (apiName) => {
    if (!window.confirm(`Delete API "${apiName}"? This cannot be undone.`)) return;
    
    try {
      const response = await axios.delete(`${API}/admin/settings/api-config/custom/${encodeURIComponent(apiName)}?token=${token}`);
      setCustomApis(response.data.custom_apis);
    } catch (err) {
      alert('Failed to delete API: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      key="settings"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <h2 className="text-2xl font-bold text-white mb-6">Settings</h2>

      <div className="grid gap-6">
        {/* Credit Packages */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Credit Packages</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(packages).map(([id, pkg]) => (
              <div key={id} className="bg-white/5 rounded-lg p-4 border border-white/10 relative group">
                {editingPackage === id ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={pkgName}
                      onChange={(e) => setPkgName(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded px-3 py-1 text-white text-sm"
                      placeholder="Package Name"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-white/40 text-xs">Price ($)</label>
                        <input
                          type="number"
                          value={pkgPrice}
                          onChange={(e) => setPkgPrice(e.target.value)}
                          className="w-full bg-white/10 border border-white/20 rounded px-3 py-1 text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-white/40 text-xs">Credits</label>
                        <input
                          type="number"
                          value={pkgCredits}
                          onChange={(e) => setPkgCredits(e.target.value)}
                          className="w-full bg-white/10 border border-white/20 rounded px-3 py-1 text-white text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-white/40 text-xs">Bonus Credits</label>
                      <input
                        type="number"
                        value={pkgBonus}
                        onChange={(e) => setPkgBonus(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded px-3 py-1 text-white text-sm"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => setEditingPackage(null)}
                        className="flex-1 py-1 bg-white/10 text-white/60 rounded text-sm hover:bg-white/20"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSavePackage}
                        disabled={saving}
                        className="flex-1 py-1 bg-[#D4AF37] text-black rounded text-sm hover:bg-[#C4A030] disabled:opacity-50"
                      >
                        {saving ? '...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => handleEditPackage(id, pkg)}
                      className="absolute top-2 right-2 p-1 text-white/40 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <p className="text-[#D4AF37] font-semibold">{pkg.name}</p>
                    <p className="text-2xl font-bold text-white">${pkg.price}</p>
                    <p className="text-white/40 text-sm">{pkg.credits} credits + {pkg.bonus} bonus</p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Portrait Styles */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">Portrait Styles</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {['Magazine Cover', 'Red Carpet', 'Sports Action', 'Business Executive', 'Confidence Boost'].map(style => (
              <div key={style} className="bg-white/5 rounded-lg p-4 text-center">
                <p className="text-white font-medium text-sm">{style}</p>
              </div>
            ))}
          </div>
        </div>

        {/* API Configuration */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">API Configuration</h3>
            <div className="flex gap-2">
              <button
                onClick={() => { setAddingApi(!addingApi); setEditingApi(false); }}
                className="px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30"
              >
                {addingApi ? 'Cancel' : '+ Add API'}
              </button>
              <button
                onClick={() => { setEditingApi(!editingApi); setAddingApi(false); }}
                className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] rounded-lg text-sm hover:bg-[#D4AF37]/30"
              >
                {editingApi ? 'Cancel' : 'Edit Keys'}
              </button>
            </div>
          </div>

          {/* Add New API Form */}
          {addingApi && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <h4 className="text-green-400 font-medium mb-3">Add New API</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-white/60 text-sm mb-1">API Name <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    value={newApiName}
                    onChange={(e) => setNewApiName(e.target.value)}
                    placeholder="e.g., Twilio, SendGrid, Custom Service"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-green-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">API Key <span className="text-red-400">*</span></label>
                  <input
                    type="password"
                    value={newApiKey}
                    onChange={(e) => setNewApiKey(e.target.value)}
                    placeholder="Enter your API key"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-green-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={newApiDescription}
                    onChange={(e) => setNewApiDescription(e.target.value)}
                    placeholder="What is this API used for?"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-green-400 focus:outline-none"
                  />
                </div>
                <button
                  onClick={handleAddCustomApi}
                  disabled={saving || !newApiName.trim() || !newApiKey.trim()}
                  className="w-full py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:opacity-50"
                >
                  {saving ? 'Adding...' : 'Add API'}
                </button>
              </div>
            </div>
          )}

          {editingApi ? (
            <div className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-1">Stripe API Key</label>
                <input
                  type="password"
                  value={stripeKey}
                  onChange={(e) => setStripeKey(e.target.value)}
                  placeholder={apiConfig.stripe_key || 'Enter new key...'}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">OpenAI API Key</label>
                <input
                  type="password"
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                  placeholder={apiConfig.openai_key || 'Enter new key...'}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Emergent LLM Key</label>
                <input
                  type="password"
                  value={emergentKey}
                  onChange={(e) => setEmergentKey(e.target.value)}
                  placeholder={apiConfig.emergent_key || 'Enter new key...'}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Fal.ai API Key</label>
                <input
                  type="password"
                  value={falKey}
                  onChange={(e) => setFalKey(e.target.value)}
                  placeholder={apiConfig.fal_key || 'Enter new key...'}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Active Image Generation API</label>
                <select
                  value={activeApi}
                  onChange={(e) => setActiveApi(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                >
                  <option value="gemini_nano_banana">Gemini Nano Banana (Free)</option>
                  <option value="openai">OpenAI GPT-Image-1</option>
                  <option value="fal_ai">Fal.ai InstantID (Best Quality)</option>
                </select>
              </div>
              <button
                onClick={handleSaveApiConfig}
                disabled={saving}
                className="w-full py-2 bg-[#D4AF37] text-black font-semibold rounded-lg hover:bg-[#C4A030] disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save API Configuration'}
              </button>
              <p className="text-white/40 text-xs">
                * Leave fields empty to keep current values. Keys are stored securely.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Core APIs */}
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Core APIs</p>
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <span className="text-white/60">Stripe</span>
                <span className={apiConfig.stripe_key?.includes('...') ? 'text-green-400' : 'text-red-400'}>
                  {apiConfig.stripe_key || 'Not configured'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <span className="text-white/60">OpenAI</span>
                <span className={apiConfig.openai_key?.includes('...') ? 'text-green-400' : 'text-red-400'}>
                  {apiConfig.openai_key || 'Not configured'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <span className="text-white/60">Emergent LLM</span>
                <span className={apiConfig.emergent_key?.includes('...') ? 'text-green-400' : 'text-red-400'}>
                  {apiConfig.emergent_key || 'Not configured'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <span className="text-white/60">Fal.ai</span>
                <span className={apiConfig.fal_key?.includes('...') ? 'text-green-400' : 'text-red-400'}>
                  {apiConfig.fal_key || 'Not configured'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <span className="text-white/60">Active Image API</span>
                <span className="text-[#D4AF37]">
                  {apiConfig.active_image_api === 'gemini_nano_banana' ? 'Gemini Nano Banana' :
                   apiConfig.active_image_api === 'openai' ? 'OpenAI' :
                   apiConfig.active_image_api === 'fal_ai' ? 'Fal.ai InstantID' : 'Unknown'}
                </span>
              </div>

              {/* Custom APIs */}
              {customApis.length > 0 && (
                <>
                  <p className="text-white/40 text-xs uppercase tracking-wider mt-4 mb-2">Custom APIs</p>
                  {customApis.map((api) => (
                    <div key={api.name} className="flex items-center justify-between py-2 border-b border-white/10 group">
                      <div>
                        <span className="text-white">{api.name}</span>
                        {api.description && <p className="text-white/40 text-xs">{api.description}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-400">{api.masked_key}</span>
                        <button
                          onClick={() => handleDeleteCustomApi(api.name)}
                          className="p-1 text-red-400/60 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete API"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AdminDashboard;
